export type ConsentMode = 'A' | 'B'

export type ConsentEventName =
  // Flow lifecycle
  | 'consent.flow_started'
  | 'consent.flow_completed'
  // Stage lifecycle
  | 'consent.stage_entered'
  | 'consent.stage_time'
  | 'consent.drop_off'
  // Navigation
  | 'consent.back_navigation'
  | 'consent.stage_continue'
  // Primary consent
  | 'consent.primary_confirmed'
  | 'consent.primary_failed'
  // Secondary consent
  | 'consent.secondary_decision'
  // Content interaction
  | 'consent.section_expanded'
  | 'consent.section_collapsed'
  | 'consent.video_opened'
  | 'consent.scroll_depth'
  // Submission
  | 'consent.submission_failed'
  // Errors
  | 'consent.validation_error'
  | 'consent.session_timeout'
  | 'consent.load_error'

export type AnalyticsEvent = {
  name: ConsentEventName
  sessionId: string
  sessionCount: number
  flowVersion: string
  mode: ConsentMode
  timestamp: number
  payload: Record<string, unknown>
}

type Provider = (event: AnalyticsEvent) => void

const _providers: Provider[] = []
let _sessionId = ''
let _sessionCount = 0
let _flowVersion = 'unknown'
let _mode: ConsentMode = 'A'

const SESSION_ID_KEY = 'consent_analytics_session_id'
const SESSION_COUNT_KEY = 'consent_analytics_session_count'

function resolveSession(): void {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY)
    if (!id) {
      id = crypto.randomUUID()
      sessionStorage.setItem(SESSION_ID_KEY, id)
      const prev = parseInt(localStorage.getItem(SESSION_COUNT_KEY) ?? '0', 10)
      localStorage.setItem(SESSION_COUNT_KEY, String(prev + 1))
    }
    _sessionId = id
    _sessionCount = parseInt(localStorage.getItem(SESSION_COUNT_KEY) ?? '1', 10)
  } catch {
    // Storage unavailable (private mode, etc.) — use in-memory fallback
    if (!_sessionId) {
      _sessionId = crypto.randomUUID()
      _sessionCount = 1
    }
  }
}

export function configureAnalytics(opts: { flowVersion: string; mode: ConsentMode }): void {
  _flowVersion = opts.flowVersion
  _mode = opts.mode
  if (!_sessionId) resolveSession()
}

export function registerAnalyticsProvider(provider: Provider): void {
  if (!_providers.includes(provider)) {
    _providers.push(provider)
  }
}

export function track(name: ConsentEventName, payload: Record<string, unknown> = {}): void {
  if (!_sessionId) resolveSession()
  const event: AnalyticsEvent = {
    name,
    sessionId: _sessionId,
    sessionCount: _sessionCount,
    flowVersion: _flowVersion,
    mode: _mode,
    timestamp: Date.now(),
    payload,
  }
  for (const p of _providers) {
    try {
      p(event)
    } catch {
      // Provider errors must never break the app
    }
  }
}
